import {
  AiAttachmentInfo,
  AiAttachmentMimeType,
  ApdmCampaignDefinition,
  ApdmCampaignKind,
  ApdmCampaignMode,
  ApdmCampaignPreviewResult,
  ApdmCampaignStatus,
  ApdmCampaignVariantProposal,
  ApdmCampaignView,
  ApdmCampaignWizardOptionsResult,
  ApdmCampaignWizardStep,
  ApdmImportStatus,
  isAiAttachmentId,
} from '@euler/model/apdm';
import { ApdmCampaignService, ApdmService } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useRef, useState } from 'react';
import {
  applyWizardLocalStep,
  assertWizardStep,
  wizardCanConfirm,
  wizardDefinition,
  wizardPreviewCurrent,
  wizardStepInput,
} from './campaignWizard';

export interface CampaignWizardDependencies {
  campaigns: Pick<
    ApdmCampaignService,
    | 'create'
    | 'detail'
    | 'saveWizardStep'
    | 'options'
    | 'preview'
    | 'confirm'
    | 'start'
  >;
  apdm: Pick<ApdmService, 'listImports' | 'getImport' | 'writeVariants'>;
}
export enum CampaignWizardAttachmentTarget {
  Images = 'images',
  Thumbnail = 'thumbnail',
}
export interface CampaignWizardAttachmentScope {
  storeId: number;
  epoch: number;
  campaignId: string;
  version: number;
  target: CampaignWizardAttachmentTarget;
}
export interface CampaignWizardState {
  campaign: ApdmCampaignView | null;
  draft: ApdmCampaignDefinition | null;
  authorized: boolean;
  identityFrozen: boolean;
  step: ApdmCampaignWizardStep | 'review';
  dirty: ApdmCampaignWizardStep[];
  options: ApdmCampaignWizardOptionsResult | null;
  /** Published imports to choose an audience from. */
  imports: Array<{ id: string; fileName: string; visits: number }>;
  nextImportId: string | null;
  proposal: ApdmCampaignVariantProposal | null;
  preview: ApdmCampaignPreviewResult | null;
  reviewed: boolean;
  busy: boolean;
  error: string | null;
}
export function initialCampaignWizardState(): CampaignWizardState {
  return {
    campaign: null,
    draft: null,
    authorized: false,
    identityFrozen: false,
    step: ApdmCampaignWizardStep.Basics,
    dirty: [],
    options: null,
    imports: [],
    nextImportId: null,
    proposal: null,
    preview: null,
    reviewed: false,
    busy: false,
    error: null,
  };
}
export function useApdmCampaignWizard(
  storeId: number,
  campaignId?: string,
  injected?: CampaignWizardDependencies,
) {
  const factory = useServiceFactory();
  const campaigns = injected?.campaigns ?? factory.apdmCampaignService;
  const apdm = injected?.apdm ?? factory.apdmService;
  const [state, setState] = useState<CampaignWizardState>(
    initialCampaignWizardState,
  );
  const current = useRef(state);
  const epoch = useRef(0);
  const revision = useRef(0);
  const scope = useRef<number | null>(storeId);
  const working = useRef(false);
  const activeAttachment = useRef<CampaignWizardAttachmentScope | null>(null);
  const publish = (next: CampaignWizardState) => {
    current.current = next;
    setState(next);
  };
  const update = (change: Partial<CampaignWizardState>) =>
    publish({ ...current.current, ...change });
  const valid = (generation: number) =>
    scope.current === storeId && generation === epoch.current;
  const requireMutable = () => {
    const value = current.current;
    if (
      !value.authorized ||
      !value.campaign ||
      !value.draft ||
      ![ApdmCampaignStatus.Draft, ApdmCampaignStatus.Scheduled].includes(
        value.campaign.status,
      )
    )
      throw new Error('Campaign edit access unavailable');
    return value;
  };
  const run = async (
    operation: (generation: number, localRevision: number) => Promise<void>,
  ) => {
    if (working.current || scope.current !== storeId) return false;
    const generation = epoch.current;
    const localRevision = revision.current;
    working.current = true;
    update({ busy: true, error: null });
    try {
      await operation(generation, localRevision);
      return true;
    } catch {
      if (valid(generation))
        update({
          authorized: false,
          preview: null,
          proposal: null,
          reviewed: false,
          error: '操作未完成或权限／版本已变更。请重新读取草稿，再核对并保存。',
        });
      return false;
    } finally {
      if (valid(generation)) {
        working.current = false;
        update({ busy: false });
      }
    }
  };
  const catalog = async (
    generation: number,
    moreCoupons = false,
    moreImports = false,
  ) => {
    const captured = current.current;
    const options = await campaigns.options({
      afterCouponCampaignId: moreCoupons
        ? captured.options?.nextAfterCouponCampaignId ?? null
        : null,
      limit: 30,
    });
    const page = await apdm.listImports({
      afterImportId: moreImports ? captured.nextImportId : null,
      limit: 30,
    });
    const listed = page.items.map(item => item.id);
    const selected =
      captured.draft?.audience.sources.flatMap(source =>
        source.kind === 'imports' ? source.importIds : [],
      ) ?? [];
    // An import chosen earlier may sit beyond this page.
    const elsewhere = await Promise.all(
      [...new Set(selected)]
        .filter(importId => !listed.includes(importId))
        .map(importId => apdm.getImport(importId)),
    );
    if (!valid(generation)) return;
    const published = [...page.items, ...elsewhere]
      .filter(view => view.status === ApdmImportStatus.Published)
      .map(view => ({
        id: view.id,
        fileName: view.fileName,
        visits: view.outcome?.documents.visits ?? 0,
      }));
    update({
      options: {
        ...options,
        coupons:
          moreCoupons && captured.options
            ? [...captured.options.coupons, ...options.coupons]
            : options.coupons,
      },
      imports: moreImports
        ? [
            ...captured.imports.filter(
              value => !published.some(item => item.id === value.id),
            ),
            ...published,
          ]
        : published,
      nextImportId: page.nextAfterImportId,
      preview: null,
      reviewed: false,
    });
  };
  const read = async (id: string, generation: number) => {
    const detail = await campaigns.detail({ campaignId: id });
    if (!valid(generation)) return;
    if (
      detail.summary.id !== id ||
      detail.scope !== 'full' ||
      detail.capabilities?.manageCampaign !== true
    )
      throw new Error('Campaign management access unavailable');
    const view: ApdmCampaignView = {
      ...detail.draft,
      id: detail.summary.id,
      version: detail.summary.version,
      status: detail.summary.status,
    };
    revision.current++;
    update({
      campaign: view,
      draft: wizardDefinition(view),
      authorized: true,
      identityFrozen:
        detail.summary.status !== ApdmCampaignStatus.Draft ||
        detail.summary.startAt !== null,
      dirty: [],
      preview: null,
      proposal: null,
      reviewed: false,
    });
    await catalog(generation);
  };
  const load = async (id: string) => {
    if (working.current || scope.current !== storeId) return;
    epoch.current++;
    update({
      campaign: null,
      draft: null,
      authorized: false,
      preview: null,
      proposal: null,
      dirty: [],
    });
    await run(async generation => {
      await read(id, generation);
    });
  };
  const initialize = useRef<(id?: string) => void>(() => undefined);
  initialize.current = id => {
    if (id) void load(id);
    else
      void run(async generation => {
        await catalog(generation);
      });
  };
  useEffect(() => {
    const ownership = scope;
    const generation = epoch;
    ownership.current = storeId;
    generation.current++;
    revision.current++;
    working.current = false;
    const fresh = initialCampaignWizardState();
    current.current = fresh;
    setState(fresh);
    initialize.current(campaignId);
    return () => {
      ownership.current = null;
      generation.current++;
    };
  }, [storeId, campaignId, campaigns, apdm]);
  const edit = (
    step: ApdmCampaignWizardStep,
    draft: ApdmCampaignDefinition,
  ) => {
    if (scope.current !== storeId) return;
    const captured = requireMutable();
    if (
      captured.identityFrozen &&
      (draft.kind !== captured.campaign!.kind ||
        draft.mode !== captured.campaign!.mode)
    )
      throw new Error('Confirmed campaign identity is frozen');
    revision.current++;
    update({
      draft,
      dirty: [...new Set([...current.current.dirty, step])],
      preview: null,
      proposal: null,
      reviewed: false,
    });
  };
  const beginAttachment = (
    target: CampaignWizardAttachmentTarget,
  ): CampaignWizardAttachmentScope | null => {
    const captured = current.current;
    if (
      scope.current !== storeId ||
      !captured.authorized ||
      !captured.campaign ||
      !captured.draft ||
      captured.step !== ApdmCampaignWizardStep.Content ||
      ![ApdmCampaignStatus.Draft, ApdmCampaignStatus.Scheduled].includes(
        captured.campaign.status,
      )
    )
      return null;
    const ticket = {
      storeId,
      epoch: epoch.current,
      campaignId: captured.campaign.id,
      version: captured.campaign.version,
      target,
    };
    activeAttachment.current = ticket;
    return ticket;
  };
  const cancelAttachment = (ticket: CampaignWizardAttachmentScope | null) => {
    if (activeAttachment.current === ticket) activeAttachment.current = null;
  };
  const applyAttachment = (
    ticket: CampaignWizardAttachmentScope,
    info: AiAttachmentInfo,
  ): boolean => {
    const captured = current.current;
    if (
      activeAttachment.current !== ticket ||
      !valid(ticket.epoch) ||
      ticket.storeId !== storeId ||
      captured.campaign?.id !== ticket.campaignId ||
      captured.campaign.version !== ticket.version ||
      captured.step !== ApdmCampaignWizardStep.Content ||
      !captured.authorized ||
      !captured.draft ||
      working.current
    )
      return false;
    if (
      !isAiAttachmentId(info.id) ||
      ![AiAttachmentMimeType.Jpeg, AiAttachmentMimeType.Png].includes(
        info.mimeType,
      )
    )
      throw new Error('Unsupported campaign image attachment');
    const draft = captured.draft;
    if (
      ticket.target === CampaignWizardAttachmentTarget.Thumbnail &&
      !draft.content.webpageLink
    )
      return false;
    const content =
      ticket.target === CampaignWizardAttachmentTarget.Images
        ? {
            ...draft.content,
            imageAttachmentIds: [
              ...new Set([...draft.content.imageAttachmentIds, info.id]),
            ],
          }
        : {
            ...draft.content,
            webpageLink: {
              ...draft.content.webpageLink!,
              thumbnailAttachmentId: info.id,
            },
          };
    edit(ApdmCampaignWizardStep.Content, { ...draft, content });
    return true;
  };
  const create = async (
    kind: ApdmCampaignKind,
    mode: ApdmCampaignMode | null,
    title: string,
  ) => {
    await run(async generation => {
      const view = await campaigns.create({
        kind,
        ...(mode === null ? {} : { mode }),
        ...(title.trim() ? { title } : {}),
      });
      if (valid(generation)) await read(view.id, generation);
    });
  };
  const save = async (step: ApdmCampaignWizardStep) => {
    const captured = requireMutable();
    if (step === ApdmCampaignWizardStep.Audience) {
      for (const source of captured.draft!.audience.sources) {
        if (
          source.kind === 'platform' &&
          captured.options?.platformAvailable !== true
        )
          throw new Error('Platform data is unavailable');
        if (
          source.kind === 'imports' &&
          source.importIds.some(
            id => !captured.imports.some(value => value.id === id),
          )
        )
          throw new Error('Selected import is not published');
      }
    }
    if (
      step === ApdmCampaignWizardStep.Content &&
      captured.draft!.couponCampaignId &&
      !captured.options?.coupons.some(
        value => value.id === captured.draft!.couponCampaignId,
      )
    )
      throw new Error('Refresh the owned coupon catalog');
    const input = wizardStepInput(captured.campaign!, captured.draft!, step);
    assertWizardStep(input);
    return await run(async (generation, localRevision) => {
      const view = await campaigns.saveWizardStep(input);
      if (!valid(generation)) return;
      if (
        revision.current !== localRevision ||
        view.id !== input.campaignId ||
        view.version !== input.expectedVersion + 1 ||
        view.status !== ApdmCampaignStatus.Draft
      )
        throw new Error('Campaign save revision changed');
      const kindChanged =
        step === ApdmCampaignWizardStep.Basics &&
        captured.campaign!.kind !== view.kind;
      const remaining = captured.dirty.filter(
        value =>
          value !== step &&
          !(
            kindChanged &&
            [
              ApdmCampaignWizardStep.Audience,
              ApdmCampaignWizardStep.Schedule,
            ].includes(value)
          ),
      );
      let draft = wizardDefinition(view);
      for (const pending of remaining)
        draft = applyWizardLocalStep(
          draft,
          wizardStepInput(view, captured.draft!, pending),
        );
      revision.current++;
      update({
        campaign: view,
        draft,
        dirty: remaining,
        preview: null,
        proposal: null,
        reviewed: false,
      });
    });
  };
  const preview = async () => {
    const captured = requireMutable();
    if (captured.dirty.length)
      throw new Error('Save local edits before preview');
    await run(async (generation, localRevision) => {
      const result = await campaigns.preview({
        campaignId: captured.campaign!.id,
        expectedVersion: captured.campaign!.version,
      });
      if (!valid(generation) || revision.current !== localRevision) return;
      if (!wizardPreviewCurrent(captured.campaign, result, []))
        throw new Error('Stale campaign preview');
      update({ preview: result, reviewed: false });
    });
  };
  const writeVariants = async (instructions: string) => {
    const captured = requireMutable();
    if (captured.dirty.length)
      throw new Error('Save local edits before AI proposal');
    await run(async (generation, localRevision) => {
      const proposal = await apdm.writeVariants({
        campaignId: captured.campaign!.id,
        expectedVersion: captured.campaign!.version,
        instructions,
      });
      if (!valid(generation) || revision.current !== localRevision) return;
      if (
        proposal.campaignId !== captured.campaign!.id ||
        proposal.version !== captured.campaign!.version ||
        proposal.requiresConfirmation !== true
      )
        throw new Error('Stale campaign variant proposal');
      update({ proposal });
    });
  };
  const adopt = (proposal: ApdmCampaignVariantProposal) => {
    const captured = requireMutable();
    if (
      proposal !== captured.proposal ||
      proposal.version !== captured.campaign!.version ||
      proposal.campaignId !== captured.campaign!.id
    )
      throw new Error('Proposal is no longer current');
    edit(ApdmCampaignWizardStep.Content, {
      ...captured.draft!,
      variants: proposal.variants,
    });
  };
  const confirm = async (capturedPreview: ApdmCampaignPreviewResult) => {
    const captured = requireMutable();
    if (
      captured.preview !== capturedPreview ||
      !wizardCanConfirm(
        captured.campaign,
        captured.preview,
        captured.dirty,
        captured.authorized,
        captured.reviewed,
      )
    )
      throw new Error('Review a current preview before confirmation');
    await run(async (generation, localRevision) => {
      const view = await campaigns.confirm({
        campaignId: captured.campaign!.id,
        expectedVersion: captured.campaign!.version,
      });
      if (!valid(generation)) return;
      if (
        revision.current !== localRevision ||
        view.id !== captured.campaign!.id ||
        view.version !== captured.campaign!.version + 1 ||
        view.status !== ApdmCampaignStatus.Scheduled
      )
        throw new Error('Confirmation revision changed');
      revision.current++;
      update({
        campaign: view,
        draft: wizardDefinition(view),
        identityFrozen: true,
        preview: null,
        reviewed: false,
        proposal: null,
      });
    });
  };
  const launch = async (capturedView: ApdmCampaignView) => {
    if (
      capturedView !== current.current.campaign ||
      !current.current.authorized ||
      current.current.dirty.length > 0 ||
      capturedView.status !== ApdmCampaignStatus.Scheduled
    )
      throw new Error('Campaign launch reference changed');
    await run(async generation => {
      const result = await campaigns.start({
        campaignId: capturedView.id,
        expectedVersion: capturedView.version,
      });
      if (!valid(generation)) return;
      if (
        result.campaignId !== capturedView.id ||
        result.version !== capturedView.version + 1
      )
        throw new Error('Campaign launch revision changed');
      await read(capturedView.id, generation);
    });
  };
  return {
    state,
    actions: {
      load,
      create,
      edit,
      beginAttachment,
      cancelAttachment,
      applyAttachment,
      save,
      preview,
      writeVariants,
      adopt,
      confirm,
      launch,
      refreshCatalog: async (moreCoupons = false, moreImports = false) => {
        await run(async generation => {
          await catalog(generation, moreCoupons, moreImports);
        });
      },
      reviewed: (value: boolean) =>
        update({
          reviewed:
            value &&
            !!current.current.preview &&
            current.current.dirty.length === 0,
        }),
      move: (step: CampaignWizardState['step']) => {
        if (scope.current === storeId) {
          if (step !== current.current.step) activeAttachment.current = null;
          update({ step });
        }
      },
    },
  };
}
